// Blueprint System Types
export interface Blueprint {
    id: string
    createdAt: string
    updatedAt: string
    name: string
    description?: string
    category?: string
    tags: string[]
    version: number
    isPublic: boolean
    isActive: boolean
    nodeDefinitions: any[] // JSON array of node definitions
    workflowConfig: any // JSON workflow configuration
    createdByUserId: string
    projectId?: string
}

export interface BlueprintVersion {
    id: string
    createdAt: string
    blueprintId: string
    version: number
    name: string
    description?: string
    nodeDefinitions: any[]
    workflowConfig: any
    changelog?: string
    createdByUserId: string
}

export interface BlueprintMarketplace {
    id: string
    createdAt: string
    updatedAt: string
    blueprintId: string
    featured: boolean
    verified: boolean
    rating?: number
    downloadCount: number
    publishedByUserId: string
}

export interface BlueprintReview {
    id: string
    createdAt: string
    blueprintId: string
    userId: string
    rating: number
    review?: string
}

export interface NodeGroupBlueprint {
    id: string
    createdAt: string
    updatedAt: string
    name: string
    description?: string
    blueprintId: string
    nodeIds: string[]
    groupType: 'sequential' | 'parallel' | 'conditional'
    dependencies?: any
    configuration: any
    executionOrder: number
    isReusable: boolean
}

export interface WorkflowRun {
    id: string
    createdAt: string
    updatedAt: string
    completedAt?: string
    blueprintId: string
    projectId?: string
    userId: string
    status: 'pending' | 'running' | 'completed' | 'failed' | 'cancelled'
    progress: number
    parameters?: any
    results?: any
    errorMessage?: string
    executionTimeMs?: number
}

export interface NodeExecution {
    id: string
    createdAt: string
    updatedAt: string
    workflowRunId: string
    nodeId: string
    status: 'pending' | 'running' | 'completed' | 'failed' | 'skipped'
    startedAt?: string
    completedAt?: string
    input?: any
    output?: any
    errorMessage?: string
    executionTimeMs?: number
}

export interface SimulationRun {
    id: string
    createdAt: string
    updatedAt: string
    completedAt?: string
    blueprintId: string
    userId: string
    name: string
    parameters: any
    results?: any
    status: 'pending' | 'running' | 'completed' | 'failed'
    performanceMetrics?: any
}

// Extended types for frontend use
export interface BlueprintWithDetails extends Blueprint {
    marketplace?: BlueprintMarketplace
    reviews?: BlueprintReview[]
    versions?: BlueprintVersion[]
    workflowRuns?: WorkflowRun[]
    nodeGroups?: NodeGroupBlueprint[]
}

export interface WorkflowRunWithDetails extends WorkflowRun {
    blueprint: Blueprint
    nodeExecutions?: NodeExecution[]
}

export interface SimulationRunWithDetails extends SimulationRun {
    blueprint: Blueprint
}

// API Response types
export interface BlueprintsResponse {
    blueprints: BlueprintWithDetails[]
    total: number
    page: number
    limit: number
}

export interface WorkflowRunsResponse {
    workflowRuns: WorkflowRunWithDetails[]
    total: number
    page: number
    limit: number
}

export interface SimulationRunsResponse {
    simulationRuns: SimulationRunWithDetails[]
    total: number
    page: number
    limit: number
}

export interface MarketplaceBlueprintsResponse {
    blueprints: BlueprintWithDetails[]
    total: number
    page: number
    limit: number
}

// Form types for creating/updating
export interface CreateBlueprintData {
    name: string
    description?: string
    category?: string
    tags: string[]
    isPublic: boolean
    nodeDefinitions: any[]
    workflowConfig: any
    projectId?: string
}

export interface UpdateBlueprintData {
    name?: string
    description?: string
    category?: string
    tags?: string[]
    isPublic?: boolean
    nodeDefinitions?: any[]
    workflowConfig?: any
    version?: number
}

export interface CreateWorkflowRunData {
    blueprintId: string
    projectId?: string
    parameters?: any
}

export interface CreateSimulationRunData {
    blueprintId: string
    name: string
    parameters: any
}

export interface CreateNodeGroupData {
    name: string
    description?: string
    blueprintId: string
    nodeIds: string[]
    groupType: 'sequential' | 'parallel' | 'conditional'
    dependencies?: any
    configuration: any
    executionOrder?: number
    isReusable?: boolean
}

// Filter types
export interface BlueprintFilters {
    category?: string
    isPublic?: boolean
    search?: string
    tags?: string[]
    createdByUserId?: string
    projectId?: string
}

export interface WorkflowRunFilters {
    blueprintId?: string
    projectId?: string
    status?: WorkflowRun['status']
    userId?: string
}

export interface SimulationRunFilters {
    blueprintId?: string
    userId?: string
    status?: SimulationRun['status']
}

export interface MarketplaceFilters {
    category?: string
    featured?: boolean
    verified?: boolean
    search?: string
    minRating?: number
}

// Blueprint categories
export const BLUEPRINT_CATEGORIES = [
    'Data Processing',
    'AI/ML',
    'Automation',
    'Analytics',
    'Integration',
    'Workflow',
    'Testing',
    'Monitoring',
    'Security',
    'Documentation',
    'Other'
] as const

export type BlueprintCategory = typeof BLUEPRINT_CATEGORIES[number]

// Node group types
export const NODE_GROUP_TYPES = [
    'sequential',
    'parallel',
    'conditional'
] as const

export type NodeGroupType = typeof NODE_GROUP_TYPES[number]

// Status types
export const WORKFLOW_STATUSES = [
    'pending',
    'running',
    'completed',
    'failed',
    'cancelled'
] as const

export const SIMULATION_STATUSES = [
    'pending',
    'running',
    'completed',
    'failed'
] as const

export const NODE_EXECUTION_STATUSES = [
    'pending',
    'running',
    'completed',
    'failed',
    'skipped'
] as const

export type WorkflowStatus = typeof WORKFLOW_STATUSES[number]
export type SimulationStatus = typeof SIMULATION_STATUSES[number]
export type NodeExecutionStatus = typeof NODE_EXECUTION_STATUSES[number]
